import { PageEvents } from '@griply/types';
import Typography from '@mui/material/Typography';
import { useLogin, useLoginWithApple, useLoginWithGoogle } from 'features/user';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { ThirdPartySignupButtons } from 'shared/components/connected/third-party-signup-buttons';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { Paths } from 'shared/routes/paths';

import { LoginForm } from '../login-form';
import * as Styled from './login-view.style';

export const LoginView: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const isFeaturebaseSso = searchParams.get('featurebase') === 'true';
  useTrackPageVisitEvent(PageEvents.Login, undefined);

  const {
    login: loginMailPassword,
    isLoading: isLoadingMailPassword,
    error: errorMailPassword,
  } = useLogin();
  const { login: loginApple, isLoading: isLoadingApple } = useLoginWithApple();
  const { login: loginGoogle, isLoading: isLoadingGoogle } =
    useLoginWithGoogle();

  const isLoading = isLoadingMailPassword || isLoadingApple || isLoadingGoogle;

  return (
    <Styled.Container>
      <Styled.Body>
        <Styled.BodyContent>
          <Styled.Header>
            <Styled.Logo
              src="/griply-logo/griply-logo.png"
              srcSet="/griply-logo/griply-logo@2x.png 2x, /griply-logo/griply-logo@3x.png 3x"
              alt="Griply"
            />

            <Typography variant="h3" textAlign="center">
              {t(
                isFeaturebaseSso
                  ? 'forms.login.featurebase-sso-title'
                  : 'forms.login.title',
              )}
            </Typography>
          </Styled.Header>

          <Styled.Options>
            <ThirdPartySignupButtons
              isLoading={isLoading}
              onAppleLogin={loginApple}
              onGoogleLogin={loginGoogle}
            />

            <Styled.Divider />

            <LoginForm
              onSubmit={loginMailPassword}
              isLoading={isLoading}
              error={errorMailPassword ?? undefined}
            />
          </Styled.Options>
        </Styled.BodyContent>
      </Styled.Body>

      <Styled.Footer>
        <Typography variant="overline" component="span" textAlign="center">
          {t('forms.login.no-account.question.label')}{' '}
          <Styled.Anchor to={Paths.Signup}>
            {t('forms.login.no-account.link.label')}
          </Styled.Anchor>
        </Typography>

        <Styled.TermsOfUse>
          {t('forms.terms-and-agreement.1')}
          <Styled.ExternalAnchor href="https://griply.app/eula" target="_blank">
            {t('forms.terms-and-agreement.terms-of-use')}
          </Styled.ExternalAnchor>
          {t('forms.terms-and-agreement.2')}
          <Styled.ExternalAnchor
            href="https://griply.app/privacy-policy"
            target="_blank"
          >
            {t('forms.terms-and-agreement.privacy-policy')}
          </Styled.ExternalAnchor>
          {t('forms.terms-and-agreement.3')}
        </Styled.TermsOfUse>
      </Styled.Footer>
    </Styled.Container>
  );
};
